<template>
  <div class="offer-addresses-search">
    <div v-if="loading">
      <p><i>Loading address information...</i></p>
    </div>
    <div v-else-if="!offer">
      <p>
        This business does not have any addresses in the selected location,
        <a href="#"
          class="text-decoration-none"
          data-bs-toggle="modal"
          data-bs-target="#mapLocationModal">try widening your search radius or choosing a different area</a>.
      </p>
    </div>
    <div v-else>
      <div class="js-offer-addresses">
        <!-- Physical Addresses Section -->
        <div v-if="hasPhysicalType(offer)">
          <h3>Where can I visit this business?</h3>
          <hr>
          <!-- Location button -->
          <div v-if="!hasDigitalType(offer) && physicalAddresses(offer).length > 0" class="mb-3">
            <p>
              <span v-if="locationData && locationData.lat">
                Businesses within {{ locationData.radius || 25 }}km of 
                {{ locationData.address || 'your selected location' }}:
              </span>
              <span v-else>
                All of New Zealand:
              </span>
              <a href="#"
                 class="text-decoration-none"
                 data-bs-toggle="modal"
                 data-bs-target="#mapLocationModal">Update your location</a>
            </p>
          </div>
          <div v-if="physicalAddresses(offer).length > 0">
            <div v-for="address in physicalAddresses(offer)" :key="`physical-${address.id}`" class="mb-4">
              <div>
                <div class="mb-3">
                  <strong>Address:</strong> {{ addressToString(address) }}
                  <strong v-if="address.distance" class="text-muted ms-2">({{ formatDistance(address.distance) }} away)</strong>
                </div>
                <AddressContactInfo v-if="offer.addresses.length > 1" :address="address" />
              </div>
              <GoogleMapsLink :address="address" />
            </div>
          </div>
          <div v-else>
            <p v-if="locationData && locationData.lat">
              No business locations within {{ locationData.radius || 25 }}km of your selected area.
            </p>
            <p v-else>
              This business does not have physical locations listed.
            </p>
          </div>
          <!-- Additional Locations Message -->
          <div class="" v-if="totalAddressCounts.physical > physicalAddresses(offer).length">
            <p class="text-muted">
              To view more business locations
              <a href="#"
                class="text-decoration-none"
                data-bs-toggle="modal"
                data-bs-target="#mapLocationModal">try widening your search radius or choosing a different area</a>.
            </p>
          </div>
        </div>

        <!-- Delivery Addresses Section -->
        <div v-if="showDeliverySection(offer)">
          <h3 class="mt-3">Where can I get this delivered?</h3>
          <hr>
          <div v-if="nationwideAddresses(deliveryAddresses(offer))">
            <strong>Delivery area:</strong> {{ LOCATION_TEXT.DEFAULT }}, {{ LOCATION_TEXT.ALL_DISTRICTS }}
          </div>
          <div v-else-if="deliveryAddresses(offer).length > 0">
            <div v-for="address in deliveryAddresses(offer)" :key="`delivery-${address.id}`" class="mb-4">
              <div>
                <div class="mb-3">
                  <strong>Delivery area:</strong> {{ `${address.district || ''}, ${address.region || ''}` }}
                </div>
                <AddressContactInfo :address="address" />
              </div>
            </div>
          </div>
        </div>

        <!-- Service Areas Section -->
        <div v-if="hasServiceType(offer)">
          <h3 class="mt-3">What areas does this business service?</h3>
          <hr>
          <div v-if="nationwideAddresses(serviceAddresses(offer))">
            <strong>Service area:</strong> {{ LOCATION_TEXT.DEFAULT }}, {{ LOCATION_TEXT.ALL_DISTRICTS }}
          </div>
          <div v-else-if="serviceAddresses(offer).length > 0">
            <div v-for="address in serviceAddresses(offer)" :key="`service-${address.id}`" class="mb-4">
              <div>
                <div class="mb-3">
                  <strong>Service area:</strong> {{ `${address.district || ''}, ${address.region || ''}` }}
                </div>
                <AddressContactInfo :address="address" />
              </div>
            </div>
          </div>
          <div v-else>
            <i>Service area information is not available.</i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LocationService } from '../../services/location_service.js';
import { LOCATION_TEXT } from '../../constants/location.js';
import { GeoCalculations } from '../../utils/geo_calculations.js';
import AddressContactInfo from './address_contact_info.vue';
import GoogleMapsLink from './google_maps_link.vue';

export default {
  name: 'OfferAddressesSearch',
  components: {
    AddressContactInfo,
    GoogleMapsLink,
  },
  props: {
    offerId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      offer: null,
      locationData: LocationService.getCurrentLocationData(),
      LOCATION_TEXT,
      totalAddressCounts: {
        physical: 0,
        delivery: 0,
        we_come_to_you: 0,
        digital: 0
      },
    };
  },
  async mounted() {
    await this.loadOfferData();
  },
  methods: {
    getElasticsearchUrl() {
      const env = process.env.RAILS_ENV || 'development';
      if (['development', 'test'].includes(env)) {
        return 'http://localhost:9200';
      } else {
        return 'https://d7pi30f5y3.execute-api.ap-southeast-2.amazonaws.com/offers/' + env;
      }
    },

    async loadOfferData() {
      try {
        const esQuery = this.buildElasticsearchQuery();
        
        // Direct Elasticsearch query with geo-filtering
        const response = await fetch(this.getElasticsearchUrl() + '/_msearch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-ndjson'
          },
          body: esQuery
        });
        
        const data = await response.json();
        
        if (data.responses && data.responses[0] && data.responses[0].hits.hits.length > 0) {
          const hit = data.responses[0].hits.hits[0];
          this.offer = hit._source;
          
          // Process aggregations to get total counts
          if (data.responses[0].aggregations?.address_type_counts?.by_type?.buckets) {
            const buckets = data.responses[0].aggregations.address_type_counts.by_type.buckets;
            buckets.forEach(bucket => {
              if (this.totalAddressCounts.hasOwnProperty(bucket.key)) {
                this.totalAddressCounts[bucket.key] = bucket.doc_count;
              }
            });
          } else if (this.offer.addresses) {
            // Fallback: count from addresses array if no aggregations
            this.offer.addresses.forEach(address => {
              if (this.totalAddressCounts.hasOwnProperty(address.type)) {
                this.totalAddressCounts[address.type]++;
              }
            });
          }
          
          // If we have geo-filtered results, use the pre-filtered addresses with distances
          if (hit.inner_hits?.filtered_addresses?.hits?.hits) {
            const filteredAddresses = hit.inner_hits.filtered_addresses.hits.hits.map(addressHit => {
              const address = addressHit._source;
              // Add distance from ES sort if available
              if (addressHit.sort && addressHit.sort[0]) {
                address.distance = addressHit.sort[0];
              }
              return address;
            });
            
            // Replace the addresses with the pre-filtered and sorted ones
            this.offer.addresses = filteredAddresses;
          }
        }
      } catch (error) {
        console.error('Failed to load offer data:', error);
      } finally {
        this.loading = false;
      }
    },

    buildElasticsearchQuery() {
      const hasLocation = this.locationData && this.locationData.lat && this.locationData.lng;
      const radius = this.locationData?.radius || 25;
      const env = process.env.RAILS_ENV || 'development';

      if (!hasLocation) {
        // No location filtering - return all addresses
        return `{"index":"offers-${env}-v7"}
{"query":{"bool":{"filter":[{"term":{"id":${this.offerId}}}]}},"_source":["id","addresses","type"],"size":1}
`;
      }

      // Location-based filtering using ES geo queries
      const query = {
        index: `offers-${env}-v7`
      };

      const searchQuery = {
        query: {
          bool: {
            filter: [
              { term: { id: this.offerId } },
              {
                nested: {
                  path: "addresses",
                  query: {
                    bool: {
                      should: [
                        // Physical addresses within radius
                        {
                          bool: {
                            must: [
                              { term: { "addresses.type": "physical" } },
                              {
                                geo_distance: {
                                  distance: `${radius}km`,
                                  "addresses.coordinates": {
                                    lat: this.locationData.lat,
                                    lon: this.locationData.lng
                                  }
                                }
                              }
                            ]
                          }
                        },
                        // Always include non-physical addresses (delivery, service, digital)
                        {
                          terms: { "addresses.type": ["delivery", "we_come_to_you", "digital"] }
                        },
                        // Also include addresses without coordinates (for service areas without specific location)
                        {
                          bool: {
                            must_not: {
                              exists: { field: "addresses.coordinates" }
                            }
                          }
                        }
                      ],
                      minimum_should_match: 1
                    }
                  },
                  inner_hits: {
                    name: "filtered_addresses",
                    sort: [
                      {
                        _geo_distance: {
                          "addresses.coordinates": {
                            lat: this.locationData.lat,
                            lon: this.locationData.lng
                          },
                          order: "asc",
                          unit: "km",
                          mode: "min",
                          ignore_unmapped: true
                        }
                      }
                    ],
                    size: 100
                  }
                }
              }
            ]
          }
        },
        _source: ["id", "type", "addresses"],
        size: 1,
        aggs: {
          address_type_counts: {
            nested: {
              path: "addresses"
            },
            aggs: {
              by_type: {
                terms: {
                  field: "addresses.type"
                }
              }
            }
          }
        }
      };

      return JSON.stringify(query) + '\n' + JSON.stringify(searchQuery) + '\n';
    },
    
    // Address filtering methods - now simple filters since ES handles geo-filtering
    deliveryAddresses(item) {
      return item.addresses.filter(addr => addr.type === 'delivery');
    },
    
    physicalAddresses(item) {
      return item.addresses.filter(addr => addr.type === 'physical');
    },
    
    serviceAddresses(item) {
      return item.addresses.filter(addr => addr.type === 'we_come_to_you');
    },
    
    formatDistance(km) {
      return GeoCalculations.formatDistance(km);
    },
    
    // Helper methods for display logic
    showDeliverySection(item) {
      return item.type && Array.isArray(item.type) && item.type.includes('delivery');
    },

    hasPhysicalType(item) {
      return item.type && Array.isArray(item.type) && item.type.includes('physical');
    },
    
    hasServiceType(item) {
      return item.type && Array.isArray(item.type) && item.type.includes('we_come_to_you');
    },
    
    hasDigitalType(item) {
      return item.type && Array.isArray(item.type) && item.type.includes('digital');
    },
    
    nationwideAddresses(addresses) {
      return addresses.some(addr => addr.region === LOCATION_TEXT.DEFAULT);
    },
    
    addressToString(address) {
      const parts = [];
      // Support both field naming conventions
      if (address.line1 || address.address_1) parts.push(address.line1 || address.address_1);
      if (address.line2 || address.address_2) parts.push(address.line2 || address.address_2);
      if (address.suburb) parts.push(address.suburb);
      if (address.city) parts.push(address.city);
      
      // If we have address parts, return them joined
      if (parts.length > 0) {
        return parts.join(', ');
      }
      
      // If no address parts are available but we have region/district info, use that
      if (address.region && address.district) {
        if (address.district === 'All' || address.district_name === 'All') {
          return `${address.region}, All districts`;
        } else {
          return `${address.region}, ${address.district} district`;
        }
      }
      
      // For service/delivery/online addresses with no location info, show as nationwide
      if (address.type === 'delivery' || address.type === 'we_come_to_you' || address.type === 'digital' || address.type === 'online') {
        return `${LOCATION_TEXT.DEFAULT}, ${LOCATION_TEXT.ALL_DISTRICTS}`;
      }
      
      return '';
    },
    
    getUnavailableRegions(item, type) {
      const allAddresses = item.addresses.filter(addr => addr.type === type);
      const availableRegions = allAddresses.map(addr => addr.region).filter(Boolean);
      return [...new Set(availableRegions)];
    },
  },
};
</script>
